import { noop } from 'utils/noop';
import { useMemo } from 'react';
import styled from 'styled-components';
import { TimeClock16 } from 'ui/icons/aurora/16px/TimeClock16';
import { StyledInput } from 'ui/inputs/InputStyles';
import { OnSelectHandler, SelectValueType } from 'ui/selects/types';
import { SelectOptionsContainer } from 'ui/selects/Selects.styles';
import { TypeaheadSelect } from 'ui/selects/TypeaheadSelect';
import { pxToRem } from 'utils/convert';
import { TimePickerProps } from './TimePicker';
import { TimeClockFormat, TimeSeries } from './types';
import { generateTimeSeries, getDefaultTimeValue, getFormattedTime } from './utils';

const StyledTypeaheadSelect = styled(TypeaheadSelect)`
  ${StyledInput} {
    width: ${pxToRem(88)};
  }
`;

const StyledSelectOptionsContainer = styled(SelectOptionsContainer)`
  width: ${pxToRem(135)};
`;

const timeSeriesToOption = (timeSeries: TimeSeries, format: TimeClockFormat) => {
  const value = getFormattedTime(timeSeries, format);

  return {
    value,
    id: value,
    label: value,
  };
};

export type TypeaheadTimePickerProps = Omit<TimePickerProps, 'hasChevronDownIcon'>;

export const TYPEAHEAD_TIME_PICKER_ID = 'typeahead-time-picker';
export const CLOCK_ICON_ID = 'clock-icon';

export const TypeaheadTimePicker = ({
  id,
  time,
  increment = 15,
  onSelect = noop,
  inputClockFormat = '12-hour',
  placeholder = 'Select a time',
  isError = false,
  hasClockIcon = true,
  isDisabled = false,
  ...props
}: TypeaheadTimePickerProps) => {
  const options = useMemo(
    () => generateTimeSeries(increment).map((t) => timeSeriesToOption(t, inputClockFormat)),
    [increment, inputClockFormat],
  );

  const defaultValues = getDefaultTimeValue(time);

  return (
    <StyledTypeaheadSelect
      isDisabled={isDisabled}
      defaultValues={defaultValues}
      placeholder={placeholder}
      options={options}
      id={id}
      data-test={TYPEAHEAD_TIME_PICKER_ID}
      isError={isError}
      onSelect={onSelect as OnSelectHandler<SelectValueType>}
      Prefix={hasClockIcon && <TimeClock16 data-test={CLOCK_ICON_ID} />}
      maxLength={8} // max number of characters for hh:mm AM/PM time format
      emptyStateText={'No matches'}
      SelectOptionsContainer={StyledSelectOptionsContainer}
      {...props}
    />
  );
};
